import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useWeeklyDigest } from '../../_hooks/use-weekly-digest'
import {
  Card,
  Eyebrow,
  EyebrowDot,
  Headline,
  Body,
  Footer,
  CallToAction,
  RefreshButton,
  Stamp,
  QuietCard,
  QuietHeadline,
  QuietBody,
  Skeleton,
} from './styled'
import { buildStamp } from './helpers'
import { WeeklyDigestCardProps } from './types'

const FALLBACK_CTA_PATH = '/saved-listings'

/**
 * Hero card on the activity page. Shows an LLM-coached summary of the
 * renter's last 7 days, gracefully handles offline, empty, and error
 * states without blocking the rest of the page.
 *
 * @param {WeeklyDigestCardProps} props
 * @returns {JSX.Element | null}
 */
const WeeklyDigestCard = ({
  ctaPathOverride,
}: WeeklyDigestCardProps): JSX.Element | null => {
  const navigate = useNavigate()
  const { state, result, generatedAt, errorMessage, refresh } =
    useWeeklyDigest()
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)

  const handleRefresh = async (): Promise<void> => {
    setIsRefreshing(true)
    try {
      await refresh()
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleCallToAction = (): void => {
    navigate(ctaPathOverride ?? FALLBACK_CTA_PATH)
  }

  if (state === 'idle') {
    return null
  }

  if (state === 'offline') {
    return null
  }

  if (state === 'empty') {
    return (
      <QuietCard>
        <QuietHeadline>Quiet week so far</QuietHeadline>
        <QuietBody>
          Import a listing or tag a few of your saved ones and we&apos;ll start
          writing you a real recap on Sundays.
        </QuietBody>
      </QuietCard>
    )
  }

  if (state === 'loading') {
    return (
      <Card>
        <Eyebrow>
          <EyebrowDot />
          Weekly recap
        </Eyebrow>
        <Headline>Pulling your week together…</Headline>
        <Skeleton />
      </Card>
    )
  }

  if (state === 'error') {
    return (
      <Card>
        <Eyebrow>
          <EyebrowDot />
          Weekly recap
        </Eyebrow>
        <Headline>Couldn&apos;t pull this week&apos;s recap</Headline>
        <Body>{errorMessage}</Body>
        <Footer>
          <RefreshButton
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing === true}
          >
            {isRefreshing === true ? 'Retrying…' : 'Retry'}
          </RefreshButton>
        </Footer>
      </Card>
    )
  }

  if (state !== 'ready' || result === null) {
    return null
  }

  return (
    <Card>
      <Eyebrow>
        <EyebrowDot />
        Weekly recap
      </Eyebrow>
      <Headline>{result.headline}</Headline>
      <Body>{result.body}</Body>
      <Footer>
        <CallToAction type="button" onClick={handleCallToAction}>
          {result.callToAction}
        </CallToAction>
        <Stamp>{buildStamp(generatedAt)}</Stamp>
      </Footer>
    </Card>
  )
}

export default WeeklyDigestCard
